import { generateKeyBetween } from 'fractional-indexing';

export interface Ordered {
  readonly id: string;
  readonly order: string;
}

/**
 * Sorting is by the tuple (order, id), not by `order` alone.
 *
 * Two replicas inserting at the same slot while offline generate the *same* fractional
 * key, because the algorithm is deterministic. Rather than jittering the key with random
 * suffixes — which can silently produce a key that sorts outside its intended neighbours —
 * the item id breaks the tie. The result is a total order every replica computes
 * identically, with no coordination and no duplicates.
 */
export function compareOrdered(a: Ordered, b: Ordered): number {
  if (a.order !== b.order) return a.order < b.order ? -1 : 1;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

export function sortByOrder<T extends Ordered>(items: readonly T[]): T[] {
  return [...items].sort(compareOrdered);
}

/**
 * Fractional key that places an item at `index` within an already-sorted list.
 *
 * The list must not contain the item being moved; callers filter it out first so the
 * target index is interpreted against the remaining items.
 */
export function keyForPosition(sorted: readonly Ordered[], index: number): string {
  const clamped = Math.max(0, Math.min(index, sorted.length));
  const prev = clamped > 0 ? (sorted[clamped - 1]?.order ?? null) : null;
  let next = clamped < sorted.length ? (sorted[clamped]?.order ?? null) : null;

  // The list is sorted, so `prev <= next`. Equality means a concurrent insert produced
  // duplicate keys; skip ahead to the first strictly greater key so we still land in a
  // valid, generatable slot rather than throwing.
  if (prev !== null && next !== null && prev >= next) {
    next = sorted.slice(clamped).find((item) => item.order > prev)?.order ?? null;
  }

  return generateKeyBetween(prev, next);
}

/** Key that appends to the end of a sorted list. */
export function keyForAppend(sorted: readonly Ordered[]): string {
  const last = sorted.length > 0 ? (sorted[sorted.length - 1]?.order ?? null) : null;
  return generateKeyBetween(last, null);
}

/** `n` evenly spaced keys appended to a sorted list, used when seeding or importing. */
export function keysForAppend(sorted: readonly Ordered[], n: number): string[] {
  const keys: string[] = [];
  let last = sorted.length > 0 ? (sorted[sorted.length - 1]?.order ?? null) : null;
  for (let i = 0; i < n; i += 1) {
    last = generateKeyBetween(last, null);
    keys.push(last);
  }
  return keys;
}
